/**
 * @fileoverview Lead Form slice — form gönderim durumu yönetimi.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { captureError } from '../../config/sentry'
import { ENV } from '../../config/env'
import axiosInstance from '../../config/axios'

/** @typedef {'idle' | 'loading' | 'success' | 'error'} SubmitStatus */

/** @type {{ submitStatus: SubmitStatus, errorMessage: string | null }} */
const initialState = {
  submitStatus:  'idle',
  errorMessage:   null,
}

/**
 * Lead form async thunk.
 * Gerçek API çağrısı yapar ve backend cevabını döndürür.
 */
export const submitLeadForm = createAsyncThunk(
  'leadForm/submit',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('public/leads.php', formData)
      return response
    } catch (error) {
      captureError(error, { formData })
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        'Bilinmeyen hata'

      if (ENV.IS_DEV) {
        console.error('[LeadForm] Submit failed:', errorMessage)
      }

      return rejectWithValue(errorMessage)
    }
  }
)

const leadFormSlice = createSlice({
  name: 'leadForm',
  initialState,
  reducers: {
    /** Form state'ini sıfırla (başarı sonrası) */
    resetForm: (state) => {
      state.submitStatus  = 'idle'
      state.errorMessage  = null
    },
    /** Manuel status güncellemesi */
    setSubmitStatus: (state, action) => {
      state.submitStatus = action.payload
    },
    /** Manuel hata mesajı güncellemesi */
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitLeadForm.pending,   (state) => { state.submitStatus = 'loading'; state.errorMessage = null })
      .addCase(submitLeadForm.fulfilled, (state) => { state.submitStatus = 'success' })
      .addCase(submitLeadForm.rejected,  (state, action) => {
        state.submitStatus = 'error'
        state.errorMessage = action.payload ?? 'Bir hata oluştu. Lütfen tekrar deneyin.'
      })
  },
})

export const { resetForm, setSubmitStatus, setErrorMessage } = leadFormSlice.actions

// Selectors
/** @param {{ leadForm: typeof initialState }} state */
export const selectSubmitStatus  = (state) => state.leadForm.submitStatus
export const selectErrorMessage  = (state) => state.leadForm.errorMessage

export default leadFormSlice.reducer
